import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreate,
  useNotify,
  SimpleForm,
  Create,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  useTranslate,
  ReferenceInput,
  AutocompleteInput,
  Labeled,
  TextInput,
  useDataProvider,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import currentDate from "../../../utils/currentDate";
import RadioGroupX from "../common/RadioGroupX";
import DebtListGrid from "../common/DebtListGrid";
import {
  BadDebtOptionRenderer,
  badDebtFilterToQuery,
  badDebtInputText,
  badDebtMatchSuggestion,
} from "../common/BadDebtCustomSelectOption";
import { useMutation } from "react-query";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(13), maxLength(13)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateCustomer = [required(), minLength(36), maxLength(36)];

export default function BadDebtAddDialog({ open, setOpen }) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const dataProvider = useDataProvider();
  const t = useTranslate();

  // GET MAX DEBT DAY
  const { data, mutate, isLoading, error } = useMutation(
    ["settings/customer-debt-max-days", "getListNoParams"],
    () =>
      dataProvider.getListNoParams("settings/customer-debt-max-days")
  );
  // GET MAX DEBT DAY

  useEffect(() => {
    if (open) {
      mutate();
    }
  }, [open]);

  const save = useCallback(
    async (values) => {
      if (!values.bad_debts.length) {
        values.bad_debts = null;
      }
      try {
        await create(
          resource,
          { data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      // FIXME: semua dialog perlu ditambah ini
      fullWidth
      maxWidth="md"
    >
      <Create title={" "} sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>{t("mine.dialog.create_bad_debt")}</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent
            // FIXME: semua dialog perlu ditambah ini
            sx={{ alignSelf: "stretch" }}
          >
            <CodeNumberInput
              open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />

            <MuiDatePickerInput
              variant="outlined"
              source="date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
            />

            <ReferenceInput
              source="customer_id"
              reference="customers/bad-debt"
              perPage={100} // FIXME: blom dipakai di sisi backend
              sort={{ field: "customer_debts.created_at", order: "ASC" }}
              // filter={{ update: "1" }}
            >
              <AutocompleteInput
                label="mine.label.customer"
                variant="outlined"
                fullWidth
                validate={validateCustomer}
                optionText={<BadDebtOptionRenderer />}
                inputText={badDebtInputText}
                matchSuggestion={badDebtMatchSuggestion}
                filterToQuery={badDebtFilterToQuery}
              />
            </ReferenceInput>

            <RadioGroupX
              row={false}
              sx={{
                "& .MuiFormGroup-root": {
                  mb: 2,
                  //justifyContent: "space-around"
                },
              }}
              choices={[
                { id: 1, name: t("mine.label.bad_debt") },
                { id: 2, name: t("mine.label.installment") },
              ]}
            />
            <Labeled fullWidth label="mine.label.bad_debt">
              <DebtListGrid dependOn="customer_id" open={open} />
            </Labeled>
            <Labeled
              fullWidth
              label={
                "*Hanya surat jalan belum lunas lebih dari " +
                data +
                " hari yang akan muncul"
              }
            >
              <></>
            </Labeled>
            <TextInput
              label={false}
              source="bad_debts"
              disabled
              sx={{
                "div.MuiInputBase-root": {
                  height: "0px !important",
                  overflow: "hidden",
                },
              }}
            />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
}
